import React, { useState, useEffect, useRef } from 'react';
import {
  Target,
  School,
  Lock,
  CheckCircle,
  Sparkles,
  ArrowUp,
  ArrowDown,
  Star,
  Clock,
  FileText,
  ChevronRight
} from 'lucide-react';
import SectionControls from '../shared/SectionControls';

const CollegeCategorizationFlow = ({ data, onAnimationComplete, onContinue, isLastSection = false }) => {
  const [isInView, setIsInView] = useState(false);
  const [placeholdersVisible, setPlaceholdersVisible] = useState(false);
  const [cards, setCards] = useState([]);
  const [isLocked, setIsLocked] = useState(false);
  const [showInsights, setShowInsights] = useState(false);
  const [showEDStamp, setShowEDStamp] = useState(false);
  const [showStrategy, setShowStrategy] = useState(false);
  const [selectedCollege, setSelectedCollege] = useState(null);
  const [animationsComplete, setAnimationsComplete] = useState(false);
  const sectionRef = useRef(null);

  // Define the exact placement sequence - 12 total cards for 4x3 grid
  const placementSequence = [
    { name: 'Stanford', position: 'R1', delay: 0 },
    { name: 'Harvard', position: 'R2', delay: 300 },
    { name: 'UC Berkeley', position: 'T1', delay: 600 },
    { name: 'UIUC', position: 'S1', delay: 900 },
    { name: 'UCLA', position: 'T2', delay: 1200 },
    { name: 'Purdue', position: 'S2', delay: 1500 },
    { name: 'Northeastern', position: 'T3', delay: 1800 },
    { name: 'UC Davis', position: 'S3', delay: 2100 },
    // UCLA L-shaped move: T2 -> T3 -> R3 (moves into Northeastern's spot, displacing it)
    { name: 'UCLA', position: 'T3', delay: 2700, move: true, from: 'T2', moveType: 'right' }, // UCLA moves right to T3
    { name: 'UCLA', position: 'R3', delay: 3400, move: true, from: 'T3', moveType: 'up' }, // UCLA moves up to R3
    { name: 'Georgia Tech', position: 'T2', delay: 4100 }, // Fill vacated T2
    { name: 'Northeastern', position: 'T3', delay: 4400 }, // Place Northeastern back in T3
    { name: 'Rice', position: 'T4', delay: 4700 }, // Place Rice in T4
    // Rice straight move: T4 -> S4
    { name: 'Rice', position: 'S4', delay: 5300, move: true, from: 'T4', moveType: 'down' }, // Move Rice T4 to S4
    { name: 'Northwestern', position: 'T4', delay: 5900 }, // Fill vacated T4 with Northwestern
    { name: 'MIT', position: 'R4', delay: 6200 }
  ];

  // College data with colors and locations
  const collegeData = {
    'Stanford': { color: 'bg-red-400 border-red-400', location: 'Palo Alto, California', icon: '🌲' },
    'Harvard': { color: 'bg-red-400 border-red-400', location: 'Cambridge, Massachusetts', icon: '🏛️' },
    'MIT': { color: 'bg-red-400 border-red-400', location: 'Cambridge, Massachusetts', icon: '🔬' },
    'UC Berkeley': { color: 'bg-gold-400 border-gold-400', location: 'Berkeley, California', icon: '🐻' },
    'UCLA': { color: 'bg-red-400 border-red-400', location: 'Los Angeles, California', icon: '🐻' },
    'Georgia Tech': { color: 'bg-gold-400 border-gold-400', location: 'Atlanta, Georgia', icon: '🐝' },
    'Northeastern': { color: 'bg-gold-400 border-gold-400', location: 'Boston, Massachusetts', icon: '🏫' },
    'UIUC': { color: 'bg-teal-400 border-teal-400', location: 'Urbana-Champaign, Illinois', icon: '🌾' },
    'Purdue': { color: 'bg-teal-400 border-teal-400', location: 'West Lafayette, Indiana', icon: '🚂' },
    'UC Davis': { color: 'bg-teal-400 border-teal-400', location: 'Davis, California', icon: '🐄' },
    'Rice': { color: 'bg-teal-400 border-teal-400', location: 'Houston, Texas', icon: '🌾' },
    'Northwestern': { color: 'bg-gold-400 border-gold-400', location: 'Evanston, Illinois', icon: '🏫' }
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isInView) {
          setIsInView(true);
          setTimeout(() => startAnimatedSequence(), 1000);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, [isInView]);

  const startAnimatedSequence = () => {
    // Reset states
    setPlaceholdersVisible(false);
    setCards([]);
    setIsLocked(false);
    setShowInsights(false);
    setShowEDStamp(false);
    setShowStrategy(false);
    setSelectedCollege(null);

    // Show placeholder boxes
    setTimeout(() => setPlaceholdersVisible(true), 100);

    // Execute placement sequence
    placementSequence.forEach((step) => {
      setTimeout(() => {
        if (step.move) {
          // This is a move operation with direction-specific animation
          setCards(prev => {
            // Handle displacement: remove any card already at the target position
            let newCards = prev.filter(card =>
              !(card.position === step.position && card.name !== step.name)
            );

            // Update the moving card's position
            return newCards.map(card =>
              card.name === step.name ? {
                ...card,
                position: step.position,
                isMoving: true,
                moveDirection: step.moveType || 'up'
              } : card
            );
          });
          // Remove moving state after animation
          setTimeout(() => {
            setCards(prev => prev.map(card =>
              card.name === step.name ? {
                ...card,
                isMoving: false,
                moveDirection: undefined
              } : card
            ));
          }, 700);
        } else {
          // This is a new placement
          setCards(prev => {
            // Check if this card already exists (for moves)
            const exists = prev.find(c => c.name === step.name);
            if (exists) return prev;

            return [...prev, {
              name: step.name,
              position: step.position,
              ...collegeData[step.name],
              isNew: true
            }];
          });

          // Remove "new" state after animation
          setTimeout(() => {
            setCards(prev => prev.map(card =>
              card.name === step.name ? { ...card, isNew: false } : card
            ));
          }, 300);
        }
      }, 500 + step.delay);
    });

    // Lock the configuration
    setTimeout(() => {
      setIsLocked(true);
    }, 6500);

    // Show insights panel
    setTimeout(() => {
      setShowInsights(true);
    }, 7000);

    // Add ED stamp to MIT
    setTimeout(() => {
      setShowEDStamp(true);
    }, 8000);

    // Enable strategy view for MIT
    setTimeout(() => {
      setSelectedCollege('MIT');
      setShowStrategy(true);
    }, 9000);

    // Mark animations as complete
    setTimeout(() => {
      setAnimationsComplete(true);
      onAnimationComplete && onAnimationComplete();
    }, 10000);
  };

  // Helper to get position coordinates
  const getPositionStyle = (position) => {
    const positions = {
      // Reach row (top)
      'R1': { gridColumn: 1, gridRow: 1 },
      'R2': { gridColumn: 2, gridRow: 1 },
      'R3': { gridColumn: 3, gridRow: 1 },
      'R4': { gridColumn: 4, gridRow: 1 },
      // Target row (middle)
      'T1': { gridColumn: 1, gridRow: 2 },
      'T2': { gridColumn: 2, gridRow: 2 },
      'T3': { gridColumn: 3, gridRow: 2 },
      'T4': { gridColumn: 4, gridRow: 2 },
      // Safety row (bottom)
      'S1': { gridColumn: 1, gridRow: 3 },
      'S2': { gridColumn: 2, gridRow: 3 },
      'S3': { gridColumn: 3, gridRow: 3 },
      'S4': { gridColumn: 4, gridRow: 3 }
    };

    return positions[position] || {};
  };

  const tiers = [
    { name: 'reach', label: 'Reach Schools', color: 'border-red-400 bg-red-50', textColor: 'text-red-700' },
    { name: 'target', label: 'Target Schools', color: 'border-gold-300 bg-gold-50', textColor: 'text-gold-700' },
    { name: 'safety', label: 'Safety Schools', color: 'border-teal-400 bg-teal-50', textColor: 'text-teal-700' }
  ];

  // Count cards in each tier
  const getCardCounts = () => {
    const counts = { reach: 0, target: 0, safety: 0 };
    cards.forEach(card => {
      if (card.position.startsWith('R')) counts.reach++;
      else if (card.position.startsWith('T')) counts.target++;
      else if (card.position.startsWith('S')) counts.safety++;
    });
    return counts;
  };

  const cardCounts = getCardCounts();

  return (
    <div ref={sectionRef} className="max-w-6xl mx-auto px-4">
      {/* Section Header */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center gap-3 mb-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-50 rounded-full">
            <Target className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-blue-700">
              Strategic College Selection
            </span>
          </div>
        </div>
        <h2 className="text-4xl font-bold text-slate-900 mb-4">
          Student-Driven Categorization
        </h2>
        <p className="text-xl text-slate-600 max-w-3xl mx-auto">
        Organize colleges by tier, then get personalized strategy pages showing how you fit, why they'd want you, and what to do next.
        </p>
      </div>

      {/* Main Content Container - Shows either grid or strategy view */}
      {!showStrategy ? (
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Left side - College Tiers Grid */}
          <div className="lg:col-span-2">
            {/* Tiers with integrated grids */}
            <div className="space-y-8">
              {tiers.map((tier, tierIndex) => (
                <div key={tier.name}>
                  {/* Tier Header */}
                  <div className="flex items-center justify-between mb-3">
                    <h3 className={`font-semibold ${tier.textColor} uppercase tracking-wider text-sm`}>
                      {tier.label}
                    </h3>
                    {isLocked && (
                      <Lock className="w-4 h-4 text-slate-500" />
                    )}
                  </div>

                  {/* Cards for this tier */}
                  <div className="relative">
                    {/* Placeholder boxes for this tier */}
                    <div className={`grid grid-cols-4 gap-3 transition-opacity duration-500 ${
                      placeholdersVisible ? 'opacity-100' : 'opacity-0'
                    }`}>
                      {[1, 2, 3, 4].map(i => (
                        <div
                          key={`${tier.name}-placeholder-${i}`}
                          className={`h-32 border-2 border-dashed ${tier.color} rounded-xl`}
                        />
                      ))}
                    </div>

                    {/* Actual cards for this tier */}
                    <div className="absolute inset-0 grid grid-cols-4 gap-3">
                      {cards
                        .filter(card => {
                          if (tierIndex === 0) return card.position.startsWith('R');
                          if (tierIndex === 1) return card.position.startsWith('T');
                          if (tierIndex === 2) return card.position.startsWith('S');
                          return false;
                        })
                        .map((card) => {
                          const posStyle = getPositionStyle(card.position);
                          const columnIndex = parseInt(card.position.slice(1)) - 1; // R1 -> 0, R2 -> 1, etc.
                          const tierColor = tierIndex === 0 ? 'red' : tierIndex === 1 ? 'gold' : 'teal';
                          const isEDSchool = card.name === 'MIT' && showEDStamp;
                          const hasStrategy = card.name === 'MIT' && showStrategy;

                          return (
                            <div
                              key={`${card.name}-${card.position}`}
                              className={`rounded-xl border-2 shadow-md transition-all relative ${
                                card.isMoving ? 'duration-700 scale-105 z-20 shadow-2xl' : 'duration-300'
                              } ${
                                card.isNew ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
                              } ${
                                tierColor === 'red' ? 'bg-red-50 border-red-400' :
                                tierColor === 'gold' ? 'bg-gold-50 border-gold-400' :
                                'bg-teal-50 border-teal-400'
                              } ${
                                isEDSchool ? 'z-30 overflow-visible' : 'overflow-hidden'
                              } ${
                                card.isMoving && card.moveDirection ? `animate-move-${card.moveDirection}` : ''
                              }`}
                              style={{
                                gridColumn: columnIndex + 1,
                                transform: card.isNew ? 'scale(0)' : card.isMoving ? 'scale(1.05)' : 'scale(1)',
                                animation: card.isNew ? 'popIn 0.3s ease-out forwards' : 'none'
                              }}
                            >
                              {/* ED Badge - Seamlessly connected to card */}
                              {isEDSchool && (
                                <div className="absolute -top-5 -left-0.5 -right-0.5 z-50">
                                  <div className="bg-slate-800 text-white px-3 py-1.5 text-xs font-semibold tracking-wide rounded-t-xl animate-fadeIn">
                                    <div className="flex items-center justify-center gap-1.5">
                                      <CheckCircle className="w-3 h-3" />
                                      <span>EARLY DECISION</span>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Colored header bar */}
                              <div className={`${
                                tierColor === 'red' ? 'bg-red-400' :
                                tierColor === 'gold' ? 'bg-gold-400' :
                                'bg-teal-400'
                              } text-white px-3 py-2 font-bold text-sm flex items-center justify-between`}>
                                <span className="uppercase tracking-wide">{card.name}</span>
                                {card.isMoving && (
                                  <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">Moving</span>
                                )}
                              </div>

                              {/* Card body */}
                              <div className="p-3 bg-white">
                                <div className="flex items-center gap-2 text-gray-600 text-xs mb-2">
                                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                  <span>{card.location}</span>
                                </div>
                                <div className={`text-xs font-medium mt-3 flex items-center justify-between ${
                                  tierColor === 'red' ? 'text-red-600' :
                                  tierColor === 'gold' ? 'text-gold-500' :
                                  'text-teal-600'
                                }`}>
                                  <span>
                                    {tierIndex === 0 ? 'Reach School' : tierIndex === 1 ? 'Target School' : 'Safety School'}
                                  </span>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Lock Status */}
            <div className={`flex items-center justify-center gap-3 mt-6 transition-all duration-500 ${
              isLocked ? 'opacity-100' : 'opacity-0'
            }`}>
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              <span className="text-sm font-medium text-emerald-700">
                College list finalized - 14 schools strategically categorized
              </span>
            </div>
          </div>

          {/* Right side - Insights Panel */}
          <div className={`space-y-4 transition-all duration-700 ${
            showInsights ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
          }`}>
            {/* Balance Analysis */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Sparkles className="w-5 h-5 text-violet-600" />
                <h3 className="font-semibold text-slate-900">List Analysis</h3>
              </div>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Reach Schools</span>
                  <span className="text-sm font-bold text-red-600">{cardCounts.reach} schools</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Target Schools</span>
                  <span className="text-sm font-bold text-gold-600">{cardCounts.target} schools</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">Safety Schools</span>
                  <span className="text-sm font-bold text-teal-600">{cardCounts.safety} schools</span>
                </div>
                <div className="pt-3 border-t">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5" />
                    <p className="text-xs text-slate-700">
                      <strong>Well-balanced list!</strong> Strategic moves created optimal distribution.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Movement Summary */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
              <h4 className="font-semibold text-slate-900 mb-3">Strategic Moves</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-sm">
                  <ArrowUp className="w-4 h-4 text-blue-500" />
                  <span className="text-slate-700">UCLA moved from Target to Reach</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <ArrowUp className="w-4 h-4 text-blue-500" />
                  <span className="text-slate-700">Purdue moved from Safety to Target</span>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-4 italic">
                These strategic moves optimized the college list balance
              </p>
            </div>
          </div>
        </div>
      ) : (
        /* MIT Strategy View - Replaces the grid */
        <div className="space-y-6">
          {/* MIT Card - Stays visible above strategy */}
          <div className="flex justify-center">
            <div className="bg-blue-50 border-blue-400 rounded-xl border-2 shadow-md p-4 relative">
              <div className="absolute -top-5 -left-0.5 -right-0.5 z-50">
                <div className="bg-slate-800 text-white px-3 py-1.5 text-xs font-semibold tracking-wide rounded-t-xl">
                  <div className="flex items-center justify-center gap-1.5">
                    <CheckCircle className="w-3 h-3" />
                    <span>EARLY DECISION</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-col items-center gap-2">
                <span className="text-2xl">🔬</span>
                <h4 className="font-semibold text-slate-900">MIT</h4>
                <p className="text-xs text-slate-600">Cambridge, Massachusetts</p>
              </div>
            </div>
          </div>

          {/* Strategy Panel */}
          <div className="bg-white rounded-xl shadow-2xl border-2 border-slate-900 overflow-hidden animate-slideUp">
          {/* Header */}
          <div className="p-6 bg-slate-900 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div>
                  <h3 className="text-xl font-bold">MIT - Early Decision Strategy</h3>
                  <p className="text-slate-300">Your personalized application roadmap</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="bg-red-600 text-white px-3 py-1 rounded-lg text-xs font-bold">
                  EARLY DECISION
                </div>
                <Star className="w-5 h-5 text-yellow-400 fill-current" />
                <span className="text-sm">92% Match</span>
              </div>
            </div>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 p-6">
            {/* Left: Strategy Overview */}
            <div className="space-y-6">
              {/* Why MIT + ED */}
              <div className="bg-red-50 border-2 border-red-500 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Target className="w-6 h-6 text-red-600" />
                  <h4 className="font-semibold text-slate-900">Why Early Decision to MIT?</h4>
                </div>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-red-600 mt-0.5" />
                    <p className="text-sm text-slate-700">
                      <strong>40% higher acceptance rate</strong> in ED vs Regular Decision
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-red-600 mt-0.5" />
                    <p className="text-sm text-slate-700">
                      Your robotics + infrastructure work aligns with MIT's <strong>"Mens et Manus"</strong>
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-red-600 mt-0.5" />
                    <p className="text-sm text-slate-700">
                      Bridge inspection robot shows the <strong>hands-on innovation</strong> MIT values
                    </p>
                  </div>
                </div>
              </div>

              {/* Strategic Timeline */}
              <div className="bg-indigo-50 border-2 border-indigo-600 rounded-lg p-6">
                <div className="flex items-center gap-3 mb-4">
                  <Clock className="w-6 h-6 text-indigo-600" />
                  <h4 className="font-semibold text-slate-900">ED Timeline</h4>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full" />
                    <span className="text-sm text-slate-700"><strong>Oct 15:</strong> Submit bridge robot video</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full" />
                    <span className="text-sm text-slate-700"><strong>Nov 1:</strong> ED application deadline</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-indigo-600 rounded-full" />
                    <span className="text-sm text-slate-700"><strong>Dec 15:</strong> Decision notification</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Application Components */}
            <div className="space-y-6">
              {/* Unique Differentiators */}
              <div className="bg-white border-2 border-slate-200 rounded-lg p-6">
                <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <Star className="w-5 h-5 text-amber-500" />
                  Your MIT Differentiators
                </h4>
                <div className="space-y-3">
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="font-medium text-slate-900 text-sm">Hands-On Innovation</p>
                    <p className="text-xs text-slate-600 mt-1">
                      Bridge robot shows you build first, theorize later
                    </p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg">
                    <p className="font-medium text-slate-900 text-sm">Real-World Impact</p>
                    <p className="text-xs text-slate-600 mt-1">
                      Solved actual infrastructure problem in your community
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Items */}
              <div className="bg-white border-2 border-slate-200 rounded-lg p-6">
                <h4 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-slate-600" />
                  ED Application Priorities
                </h4>
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm">!</div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Maker Portfolio</p>
                      <p className="text-xs text-slate-600">Upload bridge robot documentation</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg">
                    <div className="w-8 h-8 bg-teal-600 rounded-full flex items-center justify-center text-white font-bold text-sm">1</div>
                    <div>
                      <p className="font-medium text-slate-900 text-sm">Main Essay</p>
                      <p className="text-xs text-slate-600">"What you do for pleasure"</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      )}

      <style jsx>{`
        @keyframes popIn {
          from {
            opacity: 0;
            transform: scale(0);
          }
          to {
            opacity: 1;
            transform: scale(1);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes moveUp {
          0% {
            transform: scale(1.05);
          }
          50% {
            transform: scale(1.15) translateY(-40px);
          }
          100% {
            transform: scale(1.05) translateY(-80px);
          }
        }

        @keyframes moveDown {
          0% {
            transform: scale(1.05);
          }
          50% {
            transform: scale(1.15) translateY(40px);
          }
          100% {
            transform: scale(1.05) translateY(80px);
          }
        }

        @keyframes moveRight {
          0% {
            transform: scale(1.05);
          }
          50% {
            transform: scale(1.15) translateX(40px);
          }
          100% {
            transform: scale(1.05) translateX(80px);
          }
        }

        @keyframes moveLeft {
          0% {
            transform: scale(1.05);
          }
          50% {
            transform: scale(1.15) translateX(-40px);
          }
          100% {
            transform: scale(1.05) translateX(-80px);
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.5s ease-out forwards;
        }

        .animate-slideUp {
          animation: slideUp 0.5s ease-out forwards;
        }

        .animate-move-up {
          animation: moveUp 2.0s ease-in-out forwards;
        }

        .animate-move-down {
          animation: moveDown 2.0s ease-in-out forwards;
        }

        .animate-move-right {
          animation: moveRight 2.0s ease-in-out forwards;
        }

        .animate-move-left {
          animation: moveLeft 2.0s ease-in-out forwards;
        }
      `}</style>

      {/* Section Controls */}
      <SectionControls
        onReplay={startAnimatedSequence}
        onContinue={onContinue}
        isLastSection={isLastSection}
        showControls={animationsComplete}
      />
    </div>
  );
};

export default CollegeCategorizationFlow;